import type { LucideIcon } from "lucide-react";
import {
    Check,
    ChevronRight,
    ClipboardList,
    MessageCircle,
    UserRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import { DataKeys } from "../../../types";
import { useLang } from "../../Context";
import { StaggerInView, StaggerInViewItem } from "../../tools/Animations";
import { HealthProductSuiteIcon } from "../HealthProductSuiteIcons";
import { HealthSection } from "../HealthSection";
import {
    getLocalized,
    HealthLang,
    isAimmaProductId,
    LocalizedString,
} from "../healthTypes";
import {
    HEALTH_BG_GREY,
    healthChildPageMetaLink,
    healthClinicalJourneyAccent,
    healthClinicalJourneyAgentChip,
    healthClinicalJourneyAgentChipIcon,
    healthClinicalJourneyAgentChipLabel,
    healthClinicalJourneyAgentChips,
    healthClinicalJourneyAgentSection,
    healthClinicalJourneyCard,
    healthClinicalJourneyCardCopy,
    healthClinicalJourneyCardDivider,
    healthClinicalJourneyCardHeader,
    healthClinicalJourneyCardIconGlyph,
    healthClinicalJourneyCardIconShell,
    healthClinicalJourneyCardStageTitle,
    healthClinicalJourneyCardSummary,
    healthClinicalJourneyCheckIcon,
    healthClinicalJourneyCheckIconWrap,
    healthClinicalJourneyCheckItem,
    healthClinicalJourneyChecklist,
    healthClinicalJourneyCheckText,
    healthClinicalJourneyHeader,
    healthClinicalJourneyHeaderStack,
    healthClinicalJourneyHubLinkWrap,
    healthClinicalJourneyIntro,
    healthClinicalJourneyMobileNode,
    healthClinicalJourneyMobileStep,
    healthClinicalJourneySectionWrap,
    healthClinicalJourneyStageColumn,
    healthClinicalJourneyStageGrid,
    healthClinicalJourneyStageShell,
    healthClinicalJourneyTimelineConnector,
    healthClinicalJourneyTimelineEndDot,
    healthClinicalJourneyTimelineLine,
    healthClinicalJourneyTimelineNode,
    healthClinicalJourneyTimelineRail,
    healthClinicalJourneyTimelineRailArrow,
    healthClinicalJourneyTimelineStage,
    healthClinicalJourneyTitle,
    healthFocusVisible,
    healthSectionEyebrow,
} from "../healthVisualStyles";
import { healthLocalePath } from "../aimmaDoctor/healthLocalePath";

const JOURNEY_ICONS: LucideIcon[] = [ClipboardList, MessageCircle, UserRound];
const JOURNEY_ICON_STROKE = 1.75;
const TIMELINE_ARROW_OFFSETS = ["31%", "64%"] as const;

interface HealthPatientJourneySectionProps {
    data: DataKeys;
    moduleTitleById: Record<string, string>;
}

function JourneyTimelineRail() {
    return (
        <div className={healthClinicalJourneyTimelineRail} aria-hidden="true">
            <span className={healthClinicalJourneyTimelineEndDot} />
            <span className={healthClinicalJourneyTimelineLine}>
                {TIMELINE_ARROW_OFFSETS.map((left) => (
                    <ChevronRight
                        key={left}
                        className={healthClinicalJourneyTimelineRailArrow}
                        style={{ left }}
                        strokeWidth={2.25}
                    />
                ))}
            </span>
            <span className={healthClinicalJourneyTimelineEndDot} />
        </div>
    );
}

export function HealthPatientJourneySection({
    data,
    moduleTitleById,
}: HealthPatientJourneySectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const eyebrow = getLocalized(data.eyebrow, hLang);
    const title = getLocalized(data.title, hLang);
    const intro = getLocalized(data.intro, hLang);
    const phases = (data.phases as DataKeys[]) ?? [];
    const hubLink = data.hubLink as DataKeys;
    const sectionId = "patient-journey";

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact
            style={{ backgroundColor: HEALTH_BG_GREY }}
        >
            <div className={healthClinicalJourneySectionWrap}>
                <header className={healthClinicalJourneyHeader}>
                    <div className={healthClinicalJourneyHeaderStack}>
                        <p className={healthSectionEyebrow}>{eyebrow}</p>
                        <span
                            className={healthClinicalJourneyAccent}
                            aria-hidden="true"
                        />
                        <h2
                            id={`${sectionId}-title`}
                            className={healthClinicalJourneyTitle}
                        >
                            {title}
                        </h2>
                        <p className={healthClinicalJourneyIntro}>{intro}</p>
                    </div>
                </header>

                <div className={healthClinicalJourneyStageShell}>
                    <JourneyTimelineRail />

                    <StaggerInView
                        className={healthClinicalJourneyStageGrid}
                        stagger={0.08}
                    >
                        {phases.map((phase, index) => {
                            const Icon = JOURNEY_ICONS[index] ?? ClipboardList;
                            const stageLabel = getLocalized(
                                phase.label as LocalizedString,
                                hLang
                            );
                            const touchpoints = phase.touchpoints as DataKeys[];
                            const moduleRefs =
                                (phase.moduleRefs as string[] | undefined) ?? [];

                            return (
                                <StaggerInViewItem
                                    key={phase.id as string}
                                    direction="bottom"
                                >
                                    <div className={healthClinicalJourneyStageColumn}>
                                        <div
                                            className={
                                                healthClinicalJourneyTimelineStage
                                            }
                                        >
                                            <div
                                                className={
                                                    healthClinicalJourneyTimelineNode
                                                }
                                            >
                                                {String(index + 1).padStart(2, "0")}
                                            </div>
                                        </div>

                                        <div
                                            className={
                                                healthClinicalJourneyMobileStep
                                            }
                                        >
                                            <div
                                                className={
                                                    healthClinicalJourneyMobileNode
                                                }
                                            >
                                                {String(index + 1).padStart(2, "0")}
                                            </div>
                                            <div
                                                className="h-5 w-px border-l border-dashed border-teal-300/90"
                                                aria-hidden="true"
                                            />
                                        </div>

                                        <div
                                            className={
                                                healthClinicalJourneyTimelineConnector
                                            }
                                            aria-hidden="true"
                                        />

                                        <article className={healthClinicalJourneyCard}>
                                            <div
                                                className={
                                                    healthClinicalJourneyCardHeader
                                                }
                                            >
                                                <div
                                                    className={
                                                        healthClinicalJourneyCardIconShell
                                                    }
                                                >
                                                    <Icon
                                                        className={
                                                            healthClinicalJourneyCardIconGlyph
                                                        }
                                                        strokeWidth={
                                                            JOURNEY_ICON_STROKE
                                                        }
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                                <div
                                                    className={
                                                        healthClinicalJourneyCardCopy
                                                    }
                                                >
                                                    <h3
                                                        className={
                                                            healthClinicalJourneyCardStageTitle
                                                        }
                                                    >
                                                        {stageLabel}
                                                    </h3>
                                                    <p
                                                        className={
                                                            healthClinicalJourneyCardSummary
                                                        }
                                                    >
                                                        {getLocalized(
                                                            phase.summary as LocalizedString,
                                                            hLang
                                                        )}
                                                    </p>
                                                </div>
                                            </div>

                                            <ul
                                                className={
                                                    healthClinicalJourneyChecklist
                                                }
                                            >
                                                {touchpoints.map(
                                                    (item, itemIndex) => (
                                                        <li
                                                            key={itemIndex}
                                                            className={
                                                                healthClinicalJourneyCheckItem
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    healthClinicalJourneyCheckIconWrap
                                                                }
                                                                aria-hidden="true"
                                                            >
                                                                <Check
                                                                    className={
                                                                        healthClinicalJourneyCheckIcon
                                                                    }
                                                                    strokeWidth={2.75}
                                                                />
                                                            </span>
                                                            <span
                                                                className={
                                                                    healthClinicalJourneyCheckText
                                                                }
                                                            >
                                                                {getLocalized(
                                                                    item as LocalizedString,
                                                                    hLang
                                                                )}
                                                            </span>
                                                        </li>
                                                    )
                                                )}
                                            </ul>

                                            <div
                                                className={
                                                    healthClinicalJourneyCardDivider
                                                }
                                                aria-hidden="true"
                                            />

                                            <div
                                                className={
                                                    healthClinicalJourneyAgentSection
                                                }
                                            >
                                                <div
                                                    className={
                                                        healthClinicalJourneyAgentChips
                                                    }
                                                >
                                                    {moduleRefs.map((moduleRef) => {
                                                        const productId =
                                                            isAimmaProductId(moduleRef)
                                                                ? moduleRef
                                                                : undefined;
                                                        const moduleTitle =
                                                            moduleTitleById[moduleRef];

                                                        return (
                                                            <a
                                                                key={moduleRef}
                                                                href={`#modules-${moduleRef}`}
                                                                className={`${healthClinicalJourneyAgentChip} ${healthFocusVisible}`}
                                                            >
                                                                <HealthProductSuiteIcon
                                                                    size="product"
                                                                    productId={
                                                                        productId
                                                                    }
                                                                    bare
                                                                    className={
                                                                        healthClinicalJourneyAgentChipIcon
                                                                    }
                                                                />
                                                                {moduleTitle ? (
                                                                    <span
                                                                        className={
                                                                            healthClinicalJourneyAgentChipLabel
                                                                        }
                                                                    >
                                                                        {moduleTitle}
                                                                    </span>
                                                                ) : (
                                                                    <span className="sr-only">
                                                                        {moduleRef}
                                                                    </span>
                                                                )}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </article>
                                    </div>
                                </StaggerInViewItem>
                            );
                        })}
                    </StaggerInView>
                </div>

                <div className={healthClinicalJourneyHubLinkWrap}>
                    <Link
                        to={healthLocalePath(
                            (hubLink?.href as string) ?? "/health#product-suite",
                            hLang
                        )}
                        className={`${healthChildPageMetaLink} ${healthFocusVisible}`}
                    >
                        {getLocalized(hubLink?.label as LocalizedString, hLang)}
                        <span aria-hidden="true"> →</span>
                    </Link>
                </div>
            </div>
        </HealthSection>
    );
}
